import type { ModuloVendible } from "@/lib/billing/precios"
import { PRESET_DE_SECTOR, type SectorDeLanding } from "./catalogo"

/**
 * Las páginas de sector (`/para/[sector]`): un día de trabajo, no una lista de
 * funciones. Es lo que ya hacía bien /tostadores, ahora para los seis.
 *
 * Cada momento del día enlaza a los módulos que lo resuelven, y el ejemplo de
 * precio se calcula con el preset del sector (la misma cuenta que se cobra).
 */

/** Un módulo que se vende, o el Tostador, que se activa a petición. */
export type ModuloDelDia = ModuloVendible | "tostador"

export interface Momento {
  hora: string
  titulo: string
  texto: string
  modulos: ModuloDelDia[]
}

export interface PaginaDeSector {
  slug: string
  nombre: string
  /** Una línea, para el menú y las tarjetas. */
  frase: string
  /** El tipo de negocio del alta, para el preset y el enlace al registro. */
  tipo: SectorDeLanding
  /** Los módulos que se enseñan marcados. Por defecto, el preset del tipo. */
  modulos: ModuloVendible[]
  /** Si además se habla del Tostador (a petición, sin precio). */
  conTostador?: boolean
  metaTitulo: string
  metaDescripcion: string
  antetitulo: string
  titular: string
  entradilla: string
  llamada: string
  segunda: { texto: string; href: string }
  diaTitulo: string
  dia: Momento[]
  ejemplo: { titulo: string; personas: number }
  especial: { etiqueta: string; titulo: string; texto: string; llamada: string; href: string }
  integracionesTitulo: string
  integraciones: { nombre: string; texto: string }[]
  matices: { titulo: string; texto: string }[]
  preguntas: { pregunta: string; respuesta: string }[]
  cierre: string
}

export const PAGINAS_DE_SECTOR: PaginaDeSector[] = [
  {
    slug: "hosteleria",
    frase: "Del pedido al proveedor al cierre de caja.",
    nombre: "Hostelería",
    tipo: "hosteleria",
    modulos: PRESET_DE_SECTOR.hosteleria,
    metaTitulo: "Software para hostelería: turnos, sala, caja y escandallos · Zindra",
    metaDescripcion:
      "Turnos y fichaje, carta y pedidos en sala, cocina y barra, stock, escandallos y cierre de caja para bares, restaurantes y cafeterías. Activa solo lo que uses.",
    antetitulo: "PARA BARES, RESTAURANTES, CAFETERÍAS Y OBRADORES",
    titular: "Del pedido al proveedor al cierre de caja, en un solo sitio.",
    entradilla:
      "Turnos y fichaje del equipo, carta y pedidos en sala, cocina y barra, stock, escandallos y caja. Lo que usa un restaurante, ya montado, y lo que no uses se quita.",
    llamada: "Montar Zindra para mi restaurante",
    segunda: { texto: "Empezar gratis con la carta digital", href: "/carta-digital" },
    diaTitulo: "Un día en tu restaurante",
    dia: [
      { hora: "08:30", titulo: "Llega el pedido del proveedor", texto: "Se recibe contra lo pedido, entra en el stock y su precio actualiza el coste de cada receta que lo usa.", modulos: ["compras", "inventario"] },
      { hora: "10:00", titulo: "Entra el turno de mañana", texto: "Cada uno ficha en la tablet de la barra. Ves quién falta y cómo va contra el cuadrante de la semana.", modulos: ["fichaje", "turnos"] },
      { hora: "11:30", titulo: "La cocina prepara", texto: "Las elaboraciones salen de la receta con sus cantidades, y la lista de apertura queda marcada.", modulos: ["producciones", "tareas"] },
      { hora: "13:30", titulo: "Servicio de comidas", texto: "El cliente pide desde la carta de la mesa o lo toma el camarero. Cada plato sale en la pantalla de cocina o de barra.", modulos: ["cartas_pedidos", "reservas"] },
      { hora: "23:45", titulo: "Cierre de caja", texto: "Los tickets del día, el cuadre por forma de pago y el cierre, conciliado después con el banco.", modulos: ["tickets", "banca"] },
      { hora: "Lunes", titulo: "Miras la semana", texto: "Ventas, coste de personal y margen por plato en un panel. O se lo preguntas al agente en el chat.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "Un restaurante de 12 personas, con todo", personas: 12 },
    especial: {
      etiqueta: "GRATIS, PARA SIEMPRE",
      titulo: "Empieza por tu carta digital",
      texto: "Tu carta con QR y tu ficha en la guía de restaurantes, sin pagar nada. Cuando quieras pedidos en mesa, cocina o turnos, se activan sobre la misma carta.",
      llamada: "Crear mi carta gratis",
      href: "/carta-digital",
    },
    integracionesTitulo: "Square y tu reparto siguen",
    integraciones: [
      { nombre: "Square", texto: "Las ventas y los tickets de Square entran solos en caja e informes." },
      { nombre: "Glovo", texto: "Los pedidos de reparto, con el resto de las ventas del día." },
      { nombre: "Tu banco", texto: "Los movimientos por Norma 43, para conciliar el cierre y los gastos." },
    ],
    matices: [
      { titulo: "Recetas, no productos", texto: "Producción habla de recetas, elaboraciones y escandallos." },
      { titulo: "Turnos partidos", texto: "Mañana y noche en el mismo día, con varios locales." },
      { titulo: "Mesas y salas", texto: "Las mesas solo aparecen cuando activas Sala y cartas." },
    ],
    preguntas: [
      { pregunta: "¿Tengo que cambiar de TPV?", respuesta: "Si usas Square, no: Zindra lee sus ventas y tickets. Hoy es el único TPV que se conecta." },
      { pregunta: "¿Puedo empezar solo con turnos y fichaje?", respuesta: "Sí. Actívalos solos y añade sala, stock o caja después, sobre los mismos datos." },
      { pregunta: "¿Sirve para varios locales?", respuesta: "Sí. Cada local es un centro, con su equipo, su stock y su caja." },
      { pregunta: "¿La carta digital es gratis de verdad?", respuesta: "Sí: la carta y la ficha en la guía no se cobran. Solo pagas los módulos que actives." },
    ],
    cierre: "Tu restaurante, montado en una tarde.",
  },
  {
    slug: "tiendas",
    frase: "Stock, caja y equipo de cada tienda.",
    nombre: "Tiendas",
    tipo: "tienda",
    modulos: PRESET_DE_SECTOR.tienda,
    metaTitulo: "Software para tiendas: stock, caja y turnos · Zindra",
    metaDescripcion: "Stock por tienda, compras a proveedor, caja y cierre, turnos y fichaje del equipo. Para comercios de una tienda o de varias.",
    antetitulo: "PARA COMERCIO CON LOCAL, DE UNA TIENDA O DE VARIAS",
    titular: "Stock, caja y equipo de cada tienda, sin hojas de cálculo.",
    entradilla: "Qué hay en cada tienda, qué se vendió hoy y quién trabaja mañana. Con los pedidos a proveedor y el cierre de caja en el mismo sitio.",
    llamada: "Montar Zindra para mi tienda",
    segunda: { texto: "Ver los módulos", href: "/modulos" },
    diaTitulo: "Un día en tu tienda",
    dia: [
      { hora: "09:30", titulo: "Se abre y se ficha", texto: "El equipo ficha en la tablet del mostrador y sigue la lista de apertura.", modulos: ["fichaje", "tareas"] },
      { hora: "11:00", titulo: "Llega el pedido", texto: "Se recibe contra lo pedido y entra en el stock de esa tienda.", modulos: ["compras", "inventario"] },
      { hora: "Todo el día", titulo: "Se vende", texto: "Las ventas de Square se registran con sus tickets y descuentan stock.", modulos: ["tickets"] },
      { hora: "20:30", titulo: "Cierre de caja", texto: "El cuadre por forma de pago y el resumen del día, conciliado después con el banco.", modulos: ["tickets", "banca"] },
      { hora: "Jueves", titulo: "Se publica el cuadrante", texto: "Los turnos de la semana que viene, al móvil de cada uno.", modulos: ["turnos", "ausencias"] },
    ],
    ejemplo: { titulo: "Una tienda con 6 personas", personas: 6 },
    especial: {
      etiqueta: "VARIAS TIENDAS",
      titulo: "Cada tienda es un centro",
      texto: "Con su equipo, su stock y su caja. Lo ves por separado o todo junto, y el precio no sube por tener más tiendas.",
      llamada: "Montar Zindra para mis tiendas",
      href: "/precios?sector=tienda",
    },
    integracionesTitulo: "Square y tu banco",
    integraciones: [
      { nombre: "Square", texto: "Las ventas y los tickets de Square, cada día." },
      { nombre: "Tu banco", texto: "Movimientos por Norma 43 o GoCardless." },
      { nombre: "Shopify", texto: "Si también vendes online, el mismo stock." },
    ],
    matices: [
      { titulo: "Stock por tienda", texto: "Cada local con su almacén." },
      { titulo: "Apertura y cierre", texto: "Tareas por turno, con quién las hizo." },
      { titulo: "Sin mesas ni cocina", texto: "No ves lo de hostelería si no lo usas." },
    ],
    preguntas: [
      { pregunta: "¿Tengo que cambiar de TPV?", respuesta: "Si usas Square, no: Zindra lee sus ventas. Hoy es el único TPV que se conecta." },
      { pregunta: "¿Sirve si vendo también online?", respuesta: "Sí. Con Shopify, el stock es el mismo para la tienda y la web." },
      { pregunta: "¿Y con varias tiendas?", respuesta: "Cada tienda es un centro; el precio no sube por tener más centros." },
    ],
    cierre: "Tu tienda, montada en una tarde.",
  },
  {
    slug: "ecommerce",
    frase: "Del almacén al pedido enviado, con Shopify.",
    nombre: "Ecommerce",
    tipo: "ecommerce",
    modulos: PRESET_DE_SECTOR.ecommerce,
    metaTitulo: "Software para ecommerce: stock, producción y margen por canal · Zindra",
    metaDescripcion: "Stock, compras con transporte y aranceles, producción por lotes y equipo de almacén, conectados con Shopify y NetSuite. Y el margen de cada canal con el coste real.",
    antetitulo: "PARA TIENDAS ONLINE Y MARCAS QUE FABRICAN",
    titular: "Del almacén al pedido enviado, con el coste de cada producto a la vista.",
    entradilla: "Stock, compras a proveedor, producción y equipo del almacén, conectados con Shopify. Y el margen de cada canal con el coste real, no con el de la ficha.",
    llamada: "Montar Zindra para mi tienda online",
    segunda: { texto: "Ver las integraciones", href: "/integraciones" },
    diaTitulo: "Un día en tu almacén",
    dia: [
      { hora: "08:00", titulo: "Llega la mercancía", texto: "Se recibe contra el pedido; su precio, con transporte y aranceles, actualiza el coste de cada producto.", modulos: ["compras", "inventario"] },
      { hora: "09:00", titulo: "Se produce por lotes", texto: "Órdenes de producción desde la receta, con lo que se consume y lo que sale.", modulos: ["producciones"] },
      { hora: "Todo el día", titulo: "El equipo ficha por puesto", texto: "Quién está en preparación, quién en envíos, y cuánto sale por hora.", modulos: ["fichaje", "workforce"] },
      { hora: "Cada pedido", titulo: "Entra la venta de Shopify", texto: "El pedido descuenta stock y queda listo para facturar.", modulos: ["inventario", "facturacion"] },
      { hora: "Lunes", titulo: "Miras el margen por canal", texto: "Tienda online, marketplaces, distribución: cuánto deja cada uno con el coste de verdad.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "Una marca con 10 personas", personas: 10 },
    especial: {
      etiqueta: "DENTRO DE SHOPIFY",
      titulo: "Zindra también vive en tu panel de Shopify",
      texto: "Se instala como app de Shopify y se vincula a tu empresa. Pedidos, productos y clientes llegan solos.",
      llamada: "Ver integraciones",
      href: "/integraciones",
    },
    integracionesTitulo: "Tu tienda y tu logística",
    integraciones: [
      { nombre: "Shopify", texto: "Pedidos, productos y clientes." },
      { nombre: "NetSuite", texto: "Facturas, escandallos, órdenes y costes de compra." },
      { nombre: "ShipHero", texto: "Existencias, movimientos, proveedores y órdenes de compra de tu almacén." },
    ],
    matices: [
      { titulo: "Lotes, no recetas", texto: "Producción habla de lotes y órdenes." },
      { titulo: "Coste con todo dentro", texto: "Incluye el transporte y los aranceles de la última compra." },
      { titulo: "Margen por canal", texto: "Online, marketplaces, B2B y distribución, por separado." },
    ],
    preguntas: [
      { pregunta: "¿Sustituye a Shopify?", respuesta: "No. Shopify sigue vendiendo; Zindra lleva el stock, la producción, el equipo y los números." },
      { pregunta: "¿Y si uso NetSuite?", respuesta: "Zindra lee de NetSuite clientes, facturas, escandallos, órdenes de producción y costes." },
      { pregunta: "¿Puedo empezar solo con inventario?", respuesta: "Sí. Lo demás se añade sobre el mismo stock." },
    ],
    cierre: "Tu almacén y tus números, en el mismo sitio.",
  },
  {
    slug: "servicios",
    frase: "Horas, proyectos y facturas de una agencia.",
    nombre: "Servicios",
    tipo: "servicios",
    modulos: PRESET_DE_SECTOR.servicios,
    metaTitulo: "Software para agencias y asesorías: horas, proyectos y facturas · Zindra",
    metaDescripcion: "Fichaje y teletrabajo, proyectos, chat con agentes y facturación para agencias, asesorías, consultoras y estudios.",
    antetitulo: "PARA AGENCIAS, ASESORÍAS, CONSULTORAS Y ESTUDIOS",
    titular: "Las horas, los proyectos y las facturas de tu equipo, en un sitio.",
    entradilla: "Quién trabaja en qué, cuánto tiempo le lleva y a quién se le factura. Con el chat y los proyectos en la misma app que el fichaje.",
    llamada: "Montar Zindra para mi agencia",
    segunda: { texto: "Vengo de Asana", href: "/work" },
    diaTitulo: "Una semana en tu agencia",
    dia: [
      { hora: "Lunes 09:00", titulo: "Se reparte la semana", texto: "Cada proyecto con sus tareas, responsables y fechas, en tablero o en calendario.", modulos: ["proyectos"] },
      { hora: "Cada día", titulo: "El equipo ficha desde el ordenador", texto: "En la oficina o en remoto, desde Zindra Work. El registro de jornada se hace solo.", modulos: ["fichaje", "teletrabajo"] },
      { hora: "A todas horas", titulo: "Se habla donde se trabaja", texto: "Un canal por cliente o por proyecto, con hilos y menciones.", modulos: ["chat"] },
      { hora: "Fin de mes", titulo: "Se factura", texto: "Facturas y presupuestos con su enlace para el cliente; los cobros, conciliados con el banco.", modulos: ["facturacion", "banca"] },
      { hora: "Cuando quieras", titulo: "Preguntas cómo vais", texto: "«¿Cuántas horas lleva el equipo este mes?» El agente responde con tus datos y tus permisos.", modulos: ["informes", "chat"] },
    ],
    ejemplo: { titulo: "Una agencia de 15 personas", personas: 15 },
    especial: {
      etiqueta: "VIENES DE ASANA",
      titulo: "Tus proyectos se traen solos",
      texto: "Conectas Asana, emparejas a las personas y entran proyectos, tareas, adjuntos y comentarios con su estado. Lo nuevo sigue llegando mientras convivís.",
      llamada: "Ver Zindra Work",
      href: "/work",
    },
    integracionesTitulo: "Lo que ya usas",
    integraciones: [
      { nombre: "Asana", texto: "Proyectos y tareas, con columnas y estados." },
      { nombre: "Google Calendar", texto: "Las agendas de cada uno junto a las del equipo." },
      { nombre: "Holded", texto: "Clientes, facturas y compras, importados a Facturación." },
    ],
    matices: [
      { titulo: "Clientes, no mesas", texto: "Solo ves lo que usas: clientes y proyectos, sin stock ni salas." },
      { titulo: "Trabajo en remoto", texto: "Fichaje desde el ordenador y, si lo activas, el tiempo real en remoto." },
      { titulo: "Un canal por cliente", texto: "El chat se organiza como trabajas, con el proyecto al lado." },
    ],
    preguntas: [
      { pregunta: "¿Puedo usar solo Chat y Proyectos?", respuesta: "Sí: el núcleo con Chat y Proyectos, que se pagan por persona. Lo demás, cuando lo necesites." },
      { pregunta: "¿Sirve para registrar el teletrabajo?", respuesta: "Sí. Fichaje para la jornada y Teletrabajo para la actividad mientras se está en remoto." },
      { pregunta: "¿Mis clientes ven algo?", respuesta: "Las facturas y presupuestos tienen un enlace propio para el cliente." },
    ],
    cierre: "Tu agencia, montada en una tarde.",
  },
  {
    slug: "software",
    frase: "Un equipo en remoto, con su jornada y sus proyectos.",
    nombre: "Software",
    tipo: "saas",
    modulos: PRESET_DE_SECTOR.saas,
    metaTitulo: "Para empresas de software y equipos en remoto · Zindra",
    metaDescripcion: "Fichaje y teletrabajo desde el escritorio, proyectos y chat en la misma app, y nóminas y facturas. Para equipos en remoto.",
    antetitulo: "PARA EMPRESAS DE SOFTWARE Y EQUIPOS EN REMOTO",
    titular: "Un equipo en remoto, con su jornada, sus proyectos y su chat.",
    entradilla: "Fichaje y teletrabajo desde el escritorio, proyectos y chat en la misma app, y las nóminas de cada uno. Sin cinco herramientas que no se hablan.",
    llamada: "Montar Zindra para mi equipo",
    segunda: { texto: "Conocer Zindra Work", href: "/work" },
    diaTitulo: "Una semana de tu equipo",
    dia: [
      { hora: "09:00", titulo: "Cada uno empieza desde su casa", texto: "Ficha desde la app de escritorio; mientras está fichado en remoto, queda su actividad.", modulos: ["fichaje", "teletrabajo"] },
      { hora: "09:15", titulo: "Daily en el canal del equipo", texto: "Hilos, menciones y llamadas con notas, dentro del chat.", modulos: ["chat"] },
      { hora: "Toda la semana", titulo: "Sprint en el tablero", texto: "Proyectos con tareas y subtareas, columnas propias y calendario de entregas.", modulos: ["proyectos"] },
      { hora: "Cuando haga falta", titulo: "Vacaciones sin correos", texto: "Se piden en la app, se aprueban y aparecen en el calendario del equipo.", modulos: ["ausencias"] },
      { hora: "Fin de mes", titulo: "Nóminas y facturas", texto: "Cada uno con su nómina en la app; las facturas a clientes salen de Facturación.", modulos: ["nominas", "facturacion"] },
    ],
    ejemplo: { titulo: "Un equipo de 20 personas", personas: 20 },
    especial: {
      etiqueta: "EN EL ESCRITORIO",
      titulo: "Zindra Work para Mac y Windows",
      texto: "Chat, proyectos y el botón de fichar siempre a mano, con avisos. Y, si lo activas, el registro del teletrabajo mientras se está fichado.",
      llamada: "Conocer Zindra Work",
      href: "/work",
    },
    integracionesTitulo: "Lo que ya usas",
    integraciones: [
      { nombre: "Asana", texto: "Proyectos y tareas, con su estado." },
      { nombre: "Google Calendar", texto: "Agendas y reuniones del equipo." },
      { nombre: "Factorial", texto: "La plantilla, para empezar sin darla de alta a mano." },
    ],
    matices: [
      { titulo: "Teletrabajo con límites", texto: "Solo se registra la actividad mientras la persona está fichada en remoto." },
      { titulo: "Agentes con permisos", texto: "El agente solo ve lo que puede ver quien le pregunta." },
      { titulo: "Sin turnos ni cajas", texto: "No ves lo que no usas." },
    ],
    preguntas: [
      { pregunta: "¿Qué registra Teletrabajo?", respuesta: "Tiempo, aplicaciones y webs mientras la persona está fichada en remoto. Fuera de la jornada, nada." },
      { pregunta: "¿Puedo traer mis proyectos de Asana?", respuesta: "Sí, con sus tareas, adjuntos, comentarios y estados." },
      { pregunta: "¿Dónde funciona?", respuesta: "En el navegador, en Mac y Windows, y en iPhone y Android." },
    ],
    cierre: "Tu equipo, en una sola app.",
  },
  {
    slug: "tostadores",
    frase: "Del grano verde a la bolsa vendida.",
    nombre: "Tostadores de café",
    tipo: "ecommerce",
    modulos: ["fichaje", "turnos", "ausencias", "inventario", "compras", "producciones", "facturacion", "informes"],
    conTostador: true,
    metaTitulo: "Software para tostadores de café, integrado con Shopify · Zindra",
    metaDescripcion: "Variedades de verde con su origen y su cata, curvas de tueste, empaquetado con su coste y venta en Shopify. Del grano verde a la bolsa vendida.",
    antetitulo: "PARA TOSTADORES DE CAFÉ DE ESPECIALIDAD",
    titular: "Del grano verde a la bolsa vendida, en un solo sitio.",
    entradilla: "Cada variedad con su origen, su proceso y su cata; cada tueste con su curva; cada bolsa con su coste. Y la venta en Shopify, con el stock al día.",
    llamada: "Hablar del Tostador",
    segunda: { texto: "Ver los módulos", href: "/modulos" },
    diaTitulo: "Un día en el tostadero",
    dia: [
      { hora: "Llega el verde", titulo: "El saco entra con su ficha", texto: "Origen, proceso y cata de cada variedad, con lo que costó traerlo hasta aquí.", modulos: ["tostador", "compras"] },
      { hora: "Tueste", titulo: "La tostadora manda su curva", texto: "Zindra Roaster lee la Diedrich por USB o por la red y guarda la curva de cada tanda. De otras tostadoras, la curva se sube en CSV.", modulos: ["tostador"] },
      { hora: "Empaquetado", titulo: "De tanda a bolsas", texto: "Lo tostado se empaqueta en formatos y entra en el stock con su coste.", modulos: ["producciones", "inventario"] },
      { hora: "Venta", titulo: "Shopify y mayoristas", texto: "Los pedidos online descuentan stock; los mayoristas, con su factura.", modulos: ["inventario", "facturacion"] },
      { hora: "Cada mes", titulo: "Cuánto deja cada café", texto: "Del saco de verde a la bolsa vendida, el margen de cada referencia.", modulos: ["informes"] },
    ],
    ejemplo: { titulo: "Un tostadero de 8 personas", personas: 8 },
    especial: {
      etiqueta: "ZINDRA ROASTER",
      titulo: "La tostadora habla con Zindra",
      texto: "Una app pequeña en el ordenador del tostadero que lee la tostadora por su cable USB o por la red del almacén y manda a Zindra la curva de cada tanda. Hoy lee las Diedrich; de otras máquinas, la curva se sube en CSV.",
      llamada: "Hablar del Tostador",
      href: "/contacto",
    },
    integracionesTitulo: "Tu tienda online",
    integraciones: [
      { nombre: "Shopify", texto: "Pedidos y productos, con el stock de bolsas al día." },
      { nombre: "Tu tostadora", texto: "Las Diedrich, por USB o por red con Zindra Roaster; las demás, con su curva en CSV." },
      { nombre: "Tu banco", texto: "Para conciliar compras de verde y ventas." },
    ],
    matices: [
      { titulo: "Variedades, no productos", texto: "Cada café verde con su origen, proceso y cata." },
      { titulo: "Curvas de tueste", texto: "Cada tanda guarda su curva, para repetir lo que salió bien." },
      { titulo: "Del saco a la bolsa", texto: "El coste de cada bolsa sale de su café verde." },
    ],
    preguntas: [
      { pregunta: "¿Por qué el Tostador no tiene precio?", respuesta: "Se activa a petición, porque hay que conectar tu tostadora. Te lo damos al hablar contigo." },
      { pregunta: "¿Puedo usar solo Inventario y Shopify?", respuesta: "Sí. El Tostador se añade cuando quieras, sobre el mismo stock." },
      { pregunta: "¿Sirve si también tengo cafetería?", respuesta: "Sí: actívale los módulos de hostelería a la misma empresa." },
    ],
    cierre: "Tu tostadero, del saco a la bolsa.",
  },
]

export function paginaDeSector(slug: string): PaginaDeSector | undefined {
  return PAGINAS_DE_SECTOR.find((p) => p.slug === slug)
}
